import { useCallback, useEffect, useState } from 'react'

import { notificationsApi } from '../api'
import type { ProximitySettings } from '../types'

export type ProximityPhase = 'loading' | 'ready' | 'error'

/**
 * Frozen defaults matching the backend: enabled, every category (null),
 * the discovery radius and the 30-minute notifications delay.
 */
export const DEFAULT_PROXIMITY_SETTINGS: ProximitySettings = {
  enabled: true,
  selectedCategories: null,
  radiusKm: 50,
  debounceMinutes: 30,
  position: null,
}

export interface ProximitySettingsState {
  settings: ProximitySettings | null
  phase: ProximityPhase
  error: unknown
  saving: boolean
  saveErrorKey: string | null
  retry: () => void
  update: (patch: {
    enabled?: boolean
    radiusKm?: number
    debounceMinutes?: number
    selectedCategories?: string[] | null
  }) => Promise<boolean>
}

/**
 * Server-owned proximity settings (D-074): master switch, discovery-bounds
 * radius and the per-activity-category exclusions (empty list = all on).
 * PATCH sends a partial merge and the response is authoritative, so another
 * device cannot leave the UI stale. Disabling the feature deletes the stored
 * last position server-side.
 */
export function useProximitySettings(): ProximitySettingsState {
  const [settings, setSettings] = useState<ProximitySettings | null>(null)
  const [phase, setPhase] = useState<ProximityPhase>('loading')
  const [error, setError] = useState<unknown>(null)
  const [saving, setSaving] = useState(false)
  const [saveErrorKey, setSaveErrorKey] = useState<string | null>(null)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    const controller = new AbortController()
    notificationsApi
      .getProximitySettings(controller.signal)
      .then((response) => {
        if (controller.signal.aborted) return
        setSettings(response.settings)
        setPhase('ready')
        setError(null)
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return
        setError(cause)
        setPhase('error')
      })
    return () => controller.abort()
  }, [attempt])

  const retry = useCallback(() => {
    setError(null)
    setPhase('loading')
    setAttempt((value) => value + 1)
  }, [])

  const update = useCallback(
    async (patch: {
      enabled?: boolean
      radiusKm?: number
      debounceMinutes?: number
      selectedCategories?: string[] | null
    }): Promise<boolean> => {
      if (!settings || saving) return false
      const previous = settings
      setSaveErrorKey(null)
      setSaving(true)
      setSettings({ ...previous, ...patch })
      try {
        const response = await notificationsApi.updateProximitySettings(patch)
        setSettings(response.settings)
        return true
      } catch {
        setSettings(previous)
        setSaveErrorKey('notifications.proximity.saveError')
        return false
      } finally {
        setSaving(false)
      }
    },
    [settings, saving],
  )

  return { settings, phase, error, saving, saveErrorKey, retry, update }
}
